// Attach-to-any-entity document hooks for the capture command room. Documents
// are tesoro artifacts linked to a polymorphic (entity_type, entity_id) — a
// capture project, an RFI, an interaction, or a workstream item. Upload reuses
// the existing presigned-PUT pipeline (uploadAttachmentViaTesoro), then links
// the resulting artifact to the entity.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/authContext';
import { artifactClient } from '../../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../../lib/uploadAttachment';
const KEY = 'entity-artifacts';
/** List the artifacts linked to one capture entity. */ export function useEntityArtifacts(entityType, entityId) {
    return useQuery({
        queryKey: [
            KEY,
            entityType,
            entityId
        ],
        enabled: !!entityType && !!entityId,
        queryFn: async ()=>{
            const resp = await artifactClient.listEntityArtifacts({
                entityType,
                entityId
            });
            return resp.artifacts;
        }
    });
}
/** Upload a file and link the resulting artifact to the entity. */ export function useUploadEntityDocument(entityType, entityId) {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (file)=>{
            if (!user || !user.tenant_id) throw new Error('Not signed in');
            const uploaded = await uploadAttachmentViaTesoro(file, {
                tenantId: user.tenant_id,
                ownerUserId: user.id,
                autoProcess: false
            });
            await artifactClient.linkArtifactToEntity({
                artifactId: uploaded.artifactId,
                entityType,
                entityId
            });
            return uploaded;
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    KEY,
                    entityType,
                    entityId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to upload document');
        }
    });
}
/** Set a document's user-facing classification (type + free-form label). */ export function useClassifyArtifact(entityType, entityId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (args)=>artifactClient.classifyArtifact(args),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    KEY,
                    entityType,
                    entityId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to classify document');
        }
    });
}
/** Detach a document from the entity (the artifact itself is not deleted). */ export function useUnlinkEntityArtifact(entityType, entityId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (artifactId)=>artifactClient.unlinkArtifactFromEntity({
                artifactId,
                entityType,
                entityId
            }),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    KEY,
                    entityType,
                    entityId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove document');
        }
    });
}
/** Open an artifact's bytes in a new tab via a short-lived presigned URL. */ export async function openArtifact(artifactId) {
    try {
        const resp = await artifactClient.generateDownloadUrl({
            artifactId
        });
        if (resp.url) window.open(resp.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to open document');
    }
}
